import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { UserPlus, Mail, Copy, Check, KeyRound, UserMinus, UserCheck, UserX, ShieldAlert, MoreVertical } from 'lucide-react';
import { Dropdown, DropdownItem } from '@/components/ui/Dropdown';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/auth';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useToast } from '@/hooks/useToast';
import { apiError } from '@/lib/api';
import {
  useUsers,
  useInviteUser,
  useUpdateUser,
  useRemoveUserFromOrg,
  useGenerateResetLink,
  useResetUserMfa,
} from '@/api/users';
import { format } from 'date-fns';
import type { Role, User } from '@/types/api';

const ROLE_LABEL: Record<string, string> = {
  client_admin: 'Admin',
  client_user: 'User',
};

export function WorkspaceTeamPage() {
  usePageTitle('Team');
  const { orgId = '' } = useParams<{ orgId: string }>();
  const { user, hasRole } = useAuth();
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const toast = useToast();
  const inviteUser = useInviteUser();
  const updateUser = useUpdateUser();
  const removeFromOrg = useRemoveUserFromOrg();
  const generateResetLink = useGenerateResetLink();
  const resetMfa = useResetUserMfa();

  const effectiveOrgId = orgId || activeOrgId || user?.organization_id || '';
  const users = useUsers({ organization_id: effectiveOrgId, limit: 200, offset: 0 });

  const isAdmin = hasRole(['client_admin', 'sysadmin', 'manager', 'staff']);

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (ids: string[]) => {
    setSelected((prev) => prev.size === ids.length ? new Set() : new Set(ids));
  };

  const handleBulkDisable = async () => {
    const targets = members.filter((m) => selected.has(m.id) && !m.is_disabled && m.id !== user?.id);
    for (const m of targets) {
      await updateUser.mutateAsync({ id: m.id, is_disabled: true }).catch(() => {});
    }
    toast.success(`${targets.length} user(s) disabled`);
    setSelected(new Set());
  };

  const handleBulkEnable = async () => {
    const targets = members.filter((m) => selected.has(m.id) && m.is_disabled);
    for (const m of targets) {
      await updateUser.mutateAsync({ id: m.id, is_disabled: false }).catch(() => {});
    }
    toast.success(`${targets.length} user(s) enabled`);
    setSelected(new Set());
  };

  const handleBulkRemove = async () => {
    const targets = members.filter((m) => selected.has(m.id) && m.id !== user?.id);
    for (const m of targets) {
      await removeFromOrg.mutateAsync({ userId: m.id, orgId: effectiveOrgId }).catch(() => {});
    }
    toast.success(`${targets.length} user(s) removed`);
    setSelected(new Set());
  };

  type ActionType = 'remove' | 'reset-mfa';
  const [actionModal, setActionModal] = useState<{ type: ActionType; user: User } | null>(null);
  const [resetLinkModal, setResetLinkModal] = useState<{ user: User; url: string } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const handleInvite = async () => {
    try {
      await inviteUser.mutateAsync({
        email: email.trim().toLowerCase(),
        role: 'client_user' as Role,
        organization_id: effectiveOrgId,
      });
      toast.success('Invitation sent', email.trim());
      setOpen(false);
      setEmail('');
      setName('');
      users.refetch();
    } catch (e) {
      toast.error('Invite failed', apiError(e));
    }
  };

  const handleChangeRole = async (m: User) => {
    const newRole: Role = m.role === 'client_admin' ? 'client_user' : 'client_admin';
    try {
      await updateUser.mutateAsync({ id: m.id, role: newRole });
      toast.success(`Role changed to ${ROLE_LABEL[newRole] ?? newRole}`);
    } catch (e) {
      toast.error('Failed to change role', apiError(e));
    }
  };

  const handleToggleDisable = async (m: User) => {
    try {
      await updateUser.mutateAsync({ id: m.id, is_disabled: !m.is_disabled });
      toast.success(m.is_disabled ? 'User enabled' : 'User disabled');
    } catch (e) {
      toast.error('Failed to update user', apiError(e));
    }
  };

  const handleResetPassword = async (m: User) => {
    try {
      const res = await generateResetLink.mutateAsync(m.id);
      setResetLinkModal({ user: m, url: res.reset_url });
      setLinkCopied(false);
    } catch (e) {
      toast.error('Failed to generate reset link', apiError(e));
    }
  };

  const handleRemove = async () => {
    if (!actionModal || actionModal.type !== 'remove') return;
    try {
      await removeFromOrg.mutateAsync({ userId: actionModal.user.id, orgId: effectiveOrgId });
      toast.success('User removed from organization');
      setActionModal(null);
    } catch (e) {
      toast.error('Failed to remove user', apiError(e));
    }
  };

  const handleResetMfa = async () => {
    if (!actionModal || actionModal.type !== 'reset-mfa') return;
    try {
      await resetMfa.mutateAsync(actionModal.user.id);
      toast.success('2FA cleared — user will set it up on next login');
      setActionModal(null);
    } catch (e) {
      toast.error('Failed to reset 2FA', apiError(e));
    }
  };

  const copyLink = async () => {
    if (!resetLinkModal) return;
    try {
      await navigator.clipboard.writeText(resetLinkModal.url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    } catch {
      toast.error('Copy failed — please copy the link manually');
    }
  };

  const members = users.data?.items ?? [];

  return (
    <AppShell crumbs={[{ label: 'Team' }]}>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-[#12122C]">Team</h1>
            <p className="text-xs text-[#676687] mt-0.5">Manage users in your organization.</p>
          </div>
          {isAdmin && (
            <Button variant="primary" onClick={() => setOpen(true)} className="w-auto text-xs">
              <UserPlus size={14} /> Invite user
            </Button>
          )}
        </div>

        <Card className="overflow-hidden" padded={false}>
          {users.isLoading ? (
            <div className="flex justify-center py-12"><Spinner size={24} /></div>
          ) : members.length === 0 ? (
            <div className="p-5">
              <EmptyState
                title="No team members yet"
                description="Invite users to give them access to your workspace."
                icon={<UserPlus size={24} />}
                action={
                  isAdmin ? (
                    <Button variant="primary" onClick={() => setOpen(true)} className="w-auto text-xs">
                      <UserPlus size={14} /> Invite user
                    </Button>
                  ) : undefined
                }
              />
            </div>
          ) : (
            <>
              {/* Bulk action bar */}
              {isAdmin && selected.size > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-[#EFF0FF] border-b border-[#C7C9F0] flex-wrap">
                  <span className="text-xs font-semibold text-[#0410BD]">{selected.size} selected</span>
                  <button
                    type="button"
                    onClick={handleBulkEnable}
                    disabled={updateUser.isPending}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-white border border-[#C7C9F0] text-[#0410BD] hover:bg-[#F2F2FF] disabled:opacity-50 transition-colors"
                  >
                    <UserCheck size={12} /> Enable
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkDisable}
                    disabled={updateUser.isPending}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-white border border-[#C7C9F0] text-[#676687] hover:bg-[#F2F2FF] disabled:opacity-50 transition-colors"
                  >
                    <UserX size={12} /> Disable
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkRemove}
                    disabled={removeFromOrg.isPending}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-white border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
                  >
                    <UserMinus size={12} /> Remove
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelected(new Set())}
                    className="ml-auto text-xs text-[#676687] hover:text-[#12122C] transition-colors"
                  >
                    Clear
                  </button>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E3E3F1] text-xs font-semibold uppercase tracking-wide text-[#676687]">
                      {isAdmin && (
                        <th className="py-2.5 pl-4 pr-2 w-8">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 accent-[#0410BD] cursor-pointer"
                            checked={selected.size === members.filter((m) => m.id !== user?.id).length && members.length > 0}
                            onChange={() => toggleSelectAll(members.filter((m) => m.id !== user?.id).map((m) => m.id))}
                          />
                        </th>
                      )}
                      <th className="py-2.5 px-4 text-left">Name</th>
                      <th className="py-2.5 px-4 text-left hidden sm:table-cell">Email</th>
                      <th className="py-2.5 px-4 text-left">Role</th>
                      <th className="py-2.5 px-4 text-left hidden md:table-cell">Joined</th>
                      <th className="py-2.5 px-4 text-left">Status</th>
                      {isAdmin && <th className="py-2.5 px-4 text-right">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m) => {
                      const isSelf = m.id === user?.id;
                      const isSelected = selected.has(m.id);
                      return (
                        <tr key={m.id} className={`border-b border-[#F2F2F8] last:border-0 transition-colors ${isSelected ? 'bg-[#F5F5FF]' : 'hover:bg-[#F9F9FC]'}`}>
                          {isAdmin && (
                            <td className="py-3 pl-4 pr-2 w-8">
                              {!isSelf && (
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleSelect(m.id)}
                                  className="h-3.5 w-3.5 accent-[#0410BD] cursor-pointer"
                                />
                              )}
                            </td>
                          )}
                          <td className="py-3 px-4 font-medium text-[#12122C]">
                            {m.full_name || '—'}
                            {isSelf && (
                              <span className="ml-2 text-[10px] text-[#676687]">(you)</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-[#676687] hidden sm:table-cell">{m.email}</td>
                          <td className="py-3 px-4">
                            <Badge tone={m.role === 'client_admin' ? 'blue' : 'gray'}>
                              {ROLE_LABEL[m.role] ?? m.role}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-[#676687] hidden md:table-cell text-xs">
                            {m.created_at ? format(new Date(m.created_at), 'PP') : '—'}
                          </td>
                          <td className="py-3 px-4">
                            <Badge tone={m.is_disabled ? 'red' : 'green'}>
                              {m.is_disabled ? 'Disabled' : 'Active'}
                            </Badge>
                          </td>
                          {isAdmin && (
                            <td className="py-3 px-4">
                              {!isSelf && (
                                <>
                                  {/* Desktop: icon buttons */}
                                  <div className="hidden sm:flex items-center gap-1 justify-end">
                                    <button
                                      type="button"
                                      title={m.role === 'client_admin' ? 'Make User' : 'Make Admin'}
                                      onClick={() => handleChangeRole(m)}
                                      disabled={updateUser.isPending}
                                      className="p-1.5 rounded-md text-[#676687] hover:bg-[#EFF0FF] hover:text-[#0410BD] disabled:opacity-40 transition-colors"
                                    >
                                      {m.role === 'client_admin' ? <UserX size={14} /> : <UserCheck size={14} />}
                                    </button>
                                    <button
                                      type="button"
                                      title={m.is_disabled ? 'Enable user' : 'Disable user'}
                                      onClick={() => handleToggleDisable(m)}
                                      disabled={updateUser.isPending}
                                      className="p-1.5 rounded-md text-[#676687] hover:bg-[#EFF0FF] hover:text-[#0410BD] disabled:opacity-40 transition-colors"
                                    >
                                      {m.is_disabled ? <UserCheck size={14} /> : <UserX size={14} />}
                                    </button>
                                    <button
                                      type="button"
                                      title="Reset password"
                                      onClick={() => handleResetPassword(m)}
                                      disabled={generateResetLink.isPending}
                                      className="p-1.5 rounded-md text-[#676687] hover:bg-[#EFF0FF] hover:text-[#0410BD] disabled:opacity-40 transition-colors"
                                    >
                                      <KeyRound size={14} />
                                    </button>
                                    <button
                                      type="button"
                                      title="Reset 2FA"
                                      onClick={() => setActionModal({ type: 'reset-mfa', user: m })}
                                      className="p-1.5 rounded-md text-[#676687] hover:bg-[#EFF0FF] hover:text-[#0410BD] transition-colors"
                                    >
                                      <ShieldAlert size={14} />
                                    </button>
                                    <button
                                      type="button"
                                      title="Remove user"
                                      onClick={() => setActionModal({ type: 'remove', user: m })}
                                      className="p-1.5 rounded-md text-[#676687] hover:bg-red-50 hover:text-red-600 transition-colors"
                                    >
                                      <UserMinus size={14} />
                                    </button>
                                  </div>
                                  {/* Mobile: dropdown */}
                                  <div className="sm:hidden flex justify-end">
                                    <Dropdown
                                      align="right"
                                      trigger={
                                        <button className="p-1.5 rounded text-[#676687] hover:text-[#0410BD] hover:bg-[#EFF0FF] transition-colors">
                                          <MoreVertical size={16} />
                                        </button>
                                      }
                                    >
                                      <DropdownItem onClick={() => handleChangeRole(m)}>
                                        {m.role === 'client_admin' ? 'Make User' : 'Make Admin'}
                                      </DropdownItem>
                                      <DropdownItem onClick={() => handleToggleDisable(m)}>
                                        {m.is_disabled ? 'Enable user' : 'Disable user'}
                                      </DropdownItem>
                                      <DropdownItem onClick={() => handleResetPassword(m)}>
                                        Reset password
                                      </DropdownItem>
                                      <DropdownItem onClick={() => setActionModal({ type: 'reset-mfa', user: m })}>
                                        Reset 2FA
                                      </DropdownItem>
                                      <DropdownItem danger onClick={() => setActionModal({ type: 'remove', user: m })}>
                                        Remove user
                                      </DropdownItem>
                                    </Dropdown>
                                  </div>
                                </>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Invite modal */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Invite a team member"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              onClick={handleInvite}
              loading={inviteUser.isPending}
              disabled={!email.trim()}
              className="w-auto"
            >
              <Mail size={14} /> Send invite
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            The invited user will receive a link to set up their account and access your workspace.
          </p>
          <div>
            <Label htmlFor="invite-email">Email address</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@example.com"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="invite-name">Name (optional)</Label>
            <Input
              id="invite-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Smith"
            />
          </div>
        </div>
      </Modal>

      {/* Confirm remove modal */}
      <Modal
        open={actionModal?.type === 'remove'}
        onClose={() => setActionModal(null)}
        title="Remove user"
        footer={
          <>
            <Button variant="secondary" onClick={() => setActionModal(null)}>Cancel</Button>
            <Button
              variant="danger"
              onClick={handleRemove}
              loading={removeFromOrg.isPending}
              className="w-auto"
            >
              Remove
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-700">
          Remove <strong>{actionModal?.user.full_name || actionModal?.user.email}</strong> from this organization? They will lose access immediately.
        </p>
      </Modal>

      {/* Confirm reset 2FA modal */}
      <Modal
        open={actionModal?.type === 'reset-mfa'}
        onClose={() => setActionModal(null)}
        title="Reset two-factor authentication"
        footer={
          <>
            <Button variant="secondary" onClick={() => setActionModal(null)}>Cancel</Button>
            <Button
              variant="primary"
              onClick={handleResetMfa}
              loading={resetMfa.isPending}
              className="w-auto"
            >
              Reset 2FA
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-700">
          This will clear 2FA for <strong>{actionModal?.user.full_name || actionModal?.user.email}</strong>. They will be prompted to set it up again on next login.
        </p>
      </Modal>

      {/* Reset password link modal */}
      <Modal
        open={!!resetLinkModal}
        onClose={() => setResetLinkModal(null)}
        title="Password reset link"
        footer={
          <Button variant="secondary" onClick={() => setResetLinkModal(null)}>Close</Button>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-700">
            Share this link with <strong>{resetLinkModal?.user.full_name || resetLinkModal?.user.email}</strong>. It expires after 24 hours.
          </p>
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-3 py-2">
            <code className="flex-1 text-xs text-[#12122C] break-all font-mono">{resetLinkModal?.url}</code>
            <button
              type="button"
              onClick={copyLink}
              className="shrink-0 text-slate-400 hover:text-[#0410BD] transition-colors ml-1"
              title="Copy link"
            >
              {linkCopied ? <Check size={15} className="text-emerald-500" /> : <Copy size={15} />}
            </button>
          </div>
        </div>
      </Modal>
    </AppShell>
  );
}
